import React, { useState, useEffect } from 'react';
import { Search, Plus, Trash2, X, Building, ChevronDown } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';

interface Hotel {
  "Hotel Name": string;
  "Area": string;
  "Star": string;
  "Status": string;
  "Hotel ID": number;
  "Address"?: string;
  "Notes"?: string;
  "Kids Min Age BF"?: number;
  "Kids BF Cost"?: number;
  "EB Min Age"?: number;
  "EB Cost"?: number;
  "Contract/Dynamic"?: string;
}

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const MultiSelectDropdown: React.FC<{
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  className?: string;
}> = ({ options, selectedValues, onChange, placeholder, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.multi-select-dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter(v => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const displayText = selectedValues.length === 0 
    ? placeholder 
    : selectedValues.length === 1 
      ? selectedValues[0]
      : `${selectedValues.length} areas selected`;

  return (
    <div className={`relative multi-select-dropdown ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 text-left border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white flex items-center justify-between"
      >
        <span className={selectedValues.length === 0 ? 'text-gray-500' : 'text-gray-900'}>
          {displayText}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map((option) => (
            <label key={option} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => toggleOption(option)}
                className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-900">{option}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const HotelManagementV2: React.FC = () => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ areas: [] as string[], star: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'rooms' | 'contracts' | 'seasons'>('details');
  const [data, setData] = useState<{rooms: any[], contracts: any[], seasons: any[]}>({
    rooms: [], contracts: [], seasons: []
  });
  const [editingItem, setEditingItem] = useState<{type: string, data?: any} | null>(null);

  useEffect(() => {
    loadHotels();
  }, []);

  useEffect(() => {
    if (selectedHotel) {
      setActiveTab('details');
      setEditingItem(null);
      loadHotelData();
    }
  }, [selectedHotel]);

  const loadHotels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/hotel-management/');
      if (response.data.success) {
        setHotels(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load hotels');
      }
    } catch (error: any) {
      console.error('Error loading hotels:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error loading hotels: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const loadHotelData = async () => {
    if (!selectedHotel) return;
    
    try {
      const [roomsRes, contractsRes, seasonsRes] = await Promise.all([
        api.get(`/hotel-management/${selectedHotel["Hotel ID"]}/rooms`),
        api.get(`/hotel-management/${selectedHotel["Hotel ID"]}/contracts`),
        api.get(`/hotel-management/${selectedHotel["Hotel ID"]}/seasons`)
      ]);
      
      setData({
        rooms: roomsRes.data.success ? roomsRes.data.data : [],
        contracts: contractsRes.data.success ? contractsRes.data.data : [],
        seasons: seasonsRes.data.success ? seasonsRes.data.data : []
      });
    } catch (error: any) {
      console.error('Error loading hotel data:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error loading hotel data: ${errorMsg}`);
    }
  };

  const saveItem = async (type: string, itemData: any) => {
    if (!selectedHotel) return;
    
    try {
      let responseData: any;

      if (type === 'contracts') {
        const file = itemData._contractFile;
        const formData = new FormData();
        formData.append('contract_name', itemData.contract_name || '');
        formData.append('contract_type', itemData.contract_type || 'REGULAR');
        formData.append('from_date', itemData.from_date || '');
        formData.append('to_date', itemData.to_date || '');
        formData.append('status', itemData.status || 'ENABLED');
        if (itemData.contract_id) formData.append('contract_id', String(itemData.contract_id));
        if (file) formData.append('contract_file', file);

        const token = sessionStorage.getItem('jwt_token');
        const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
        const res = await fetch(
          `${baseURL}/hotel-management/${selectedHotel["Hotel ID"]}/contracts`,
          {
            method: 'POST',
            headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
            body: formData,
          }
        );
        responseData = await res.json();
        console.log('Contract save response:', res.status, responseData);
        if (!res.ok) {
          console.error('Contract save failed:', res.status, JSON.stringify(responseData));
          throw new Error(responseData?.detail ? JSON.stringify(responseData.detail) : 'Failed to save contract');
        }
      } else {
        const payload = { ...itemData, hotel_id: selectedHotel["Hotel ID"] };
        const response = await api.post(`/hotel-management/${selectedHotel["Hotel ID"]}/${type}`, payload);
        responseData = response.data;
      }
      
      if (!responseData.success) {
        throw new Error(responseData.message || 'Failed to save item');
      }
      
      // If it's a contract with room prices, save them separately
      if (type === 'contracts' && itemData.room_prices && itemData.contract_id) {
        console.log('Saving room prices for contract:', itemData.contract_id);
        const pricesPayload = itemData.room_prices.map((room: any) => ({
          room_id: room.RoomID,
          base_price: room["Base Price"] || 0
        }));
        
        const pricesResponse = await api.post(
          `/hotel-management/${selectedHotel["Hotel ID"]}/contracts/${itemData.contract_id}/room-prices`,
          pricesPayload
        );
        
        if (!pricesResponse.data.success) {
          throw new Error('Contract saved but failed to update room prices: ' + (pricesResponse.data.message || 'Unknown error'));
        }
        
        console.log('Room prices saved successfully');
      }
      
      setEditingItem(null);
      await loadHotelData();
      alert('✅ Saved successfully!');
    } catch (error: any) {
      console.error(`Error saving ${type}:`, error);
      if (error?.response?.data?.detail) console.error('Validation errors:', JSON.stringify(error.response.data.detail));
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error saving ${type}: ${errorMsg}`);
    }
  };

  const deleteHotel = async (hotelId: number) => {
    if (!window.confirm('Delete this hotel? This will also delete all associated rooms, contracts, and seasons.')) return;
    
    try {
      await api.delete(`/hotel-management/${hotelId}`);
      setSelectedHotel(null);
      loadHotels();
    } catch (error) {
      console.error('Error deleting hotel:', error);
    }
  };

  const deleteItem = async (type: string, id: number) => {
    if (!selectedHotel) return;
    
    const messages = {
      rooms: 'Delete this room?',
      contracts: 'Delete this contract?', 
      seasons: 'Delete this season?'
    };
    
    if (!window.confirm(messages[type as keyof typeof messages] || 'Delete this item?')) return;
    
    try {
      const response = await api.delete(`/hotel-management/${selectedHotel["Hotel ID"]}/${type}/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete item');
      }
      await loadHotelData();
      alert('✅ Deleted successfully!');
    } catch (error: any) {
      console.error(`Error deleting ${type}:`, error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`❌ Error deleting ${type}: ${errorMsg}`);
    }
  };

  const filteredHotels = hotels.filter(hotel => {
    const matchesSearch = hotel["Hotel Name"].toLowerCase().includes(searchQuery.toLowerCase());
    const matchesArea = filters.areas.length === 0 || filters.areas.includes(hotel["Area"]);
    const matchesStar = !filters.star || hotel["Star"] === filters.star;
    const matchesStatus = !filters.status || hotel["Status"] === filters.status;
    return matchesSearch && matchesArea && matchesStar && matchesStatus;
  });

  const uniqueAreas = Array.from(new Set(hotels.map(h => h["Area"]).filter(Boolean)));
  const uniqueStars = Array.from(new Set(hotels.map(h => h["Star"]).filter(Boolean)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Panel - Hotels List */}
      <div className="w-1/2 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Hotel Management</h1>
            <button
              onClick={() => setEditingItem({type: 'hotel'})}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Hotel
            </button>
          </div>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search hotels..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <MultiSelectDropdown
                options={uniqueAreas}
                selectedValues={filters.areas}
                onChange={(values) => setFilters({...filters, areas: values})}
                placeholder="Areas"
                className="flex-1"
              />
              <SearchableSelect
                options={[{ value: '', label: 'All Stars' }, ...uniqueStars.map(star => ({ value: star, label: star }))]}
                value={filters.star}
                onChange={(value) => setFilters({...filters, star: value.toString()})}
                placeholder="Star"
                className="flex-1"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredHotels.map((hotel) => (
            <div
              key={hotel["Hotel ID"]}
              onClick={() => setSelectedHotel(hotel)}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                selectedHotel?.["Hotel ID"] === hotel["Hotel ID"] ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className="font-medium text-gray-900">{hotel["Hotel Name"]}</div>
              <div className="text-sm text-gray-500">{hotel["Area"]} • {hotel["Star"]} <span className="text-yellow-500">{'★'.repeat(parseInt(hotel["Star"].replace(' Star', '')) || 0)}</span></div>
              <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                hotel["Status"] === "ENABLED" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {hotel["Status"]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Hotel Details */}
      <div className="flex-1 flex flex-col">
        {selectedHotel ? (
          <>
            <div className="bg-white border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">{selectedHotel["Hotel Name"]}</h2>
              <p className="text-gray-600">{selectedHotel["Area"]} • {selectedHotel["Star"]} <span className="text-yellow-500">{'★'.repeat(parseInt(selectedHotel["Star"].replace(' Star', '')) || 0)}</span></p>
            </div>

            <div className="bg-white border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {[
                  { key: 'details', label: 'Details', count: 1 },
                  { key: 'rooms', label: 'Rooms', count: data.rooms.length },
                  { key: 'contracts', label: 'Contracts', count: data.contracts.length },
                  { key: 'seasons', label: 'Seasons', count: data.seasons.length }
                ].map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {label} {key !== 'details' && `(${count})`}
                  </button>
                ))}
              </nav>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'details' ? (
                <HotelDetailsForm hotel={selectedHotel} onSave={loadHotels} onDelete={() => deleteHotel(selectedHotel["Hotel ID"])} />
              ) : (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900 capitalize">{activeTab}</h3>
                    <button
                      onClick={() => setEditingItem({type: activeTab})}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add {activeTab.slice(0, -1)}
                    </button>
                  </div>

                  <DataTable
                    type={activeTab}
                    data={data[activeTab]}
                    onRowClick={(item) => setEditingItem({type: activeTab, data: item})}
                    onDelete={(id) => deleteItem(activeTab, id)}
                  />
                </>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Hotel</h3>
              <p className="text-gray-600">Choose a hotel from the list to manage its details</p>
            </div>
          </div>
        )}
      </div>

      {editingItem && editingItem.type === 'hotel' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Add New Hotel</h2>
              <button onClick={() => setEditingItem(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <AddHotelForm onSave={async () => { setEditingItem(null); await loadHotels(); }} onCancel={() => setEditingItem(null)} />
            </div>
          </div>
        </div>
      )}

      {editingItem && editingItem.type !== 'hotel' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingItem.data ? 'Edit' : 'Add'} {editingItem.type.slice(0, -1)}
              </h2>
              <button onClick={() => setEditingItem(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <ItemForm
                type={editingItem.type}
                data={editingItem.data}
                hotelId={selectedHotel?.["Hotel ID"]}
                hotel={selectedHotel || undefined}
                onSave={(data) => saveItem(editingItem.type, data)}
                onCancel={() => setEditingItem(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DataTable: React.FC<{
  type: string;
  data: any[];
  onRowClick: (item: any) => void;
  onDelete: (id: number) => void;
}> = ({ type, data, onRowClick, onDelete }) => {
  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>No {type} found. Add one to get started.</p>
      </div>
    );
  }

  const isExpired = (toDate: string) => {
    return new Date(toDate) < new Date();
  };

  if (type === 'contracts' || type === 'seasons') {
    const activeItems = data.filter(item => !isExpired(item["To Date"]));
    const pastItems = data.filter(item => isExpired(item["To Date"]));

    return (
      <div className="space-y-6">
        {/* Active Section */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Active {type}</h4>
          {activeItems.length > 0 ? (
            <ItemTable type={type} data={activeItems} onRowClick={onRowClick} onDelete={onDelete} isExpired={false} />
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              <p>No active {type} found.</p>
            </div>
          )}
        </div>

        {/* Past Section */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Past {type}</h4>
          {pastItems.length > 0 ? (
            <ItemTable type={type} data={pastItems} onRowClick={onRowClick} onDelete={onDelete} isExpired={true} />
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
              <p>No past {type} found.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <ItemTable type={type} data={data} onRowClick={onRowClick} onDelete={onDelete} isExpired={false} />;
};

const ItemTable: React.FC<{
  type: string;
  data: any[];
  onRowClick: (item: any) => void;
  onDelete: (id: number) => void;
  isExpired: boolean;
}> = ({ type, data, onRowClick, onDelete, isExpired }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            {type === 'rooms' && (
              <>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Room Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max Occupancy</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </>
            )}
            {type === 'contracts' && (
              <>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </>
            )}
            {type === 'seasons' && (
              <>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Season</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Surcharge</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((item, index) => (
            <tr 
              key={index} 
              className={`cursor-pointer ${
                isExpired 
                  ? 'bg-gray-50 text-gray-500 hover:bg-gray-100' 
                  : 'hover:bg-gray-50'
              }`} 
              onClick={() => onRowClick(item)}
            >
              {type === 'rooms' && (
                <>
                  <td className={`px-4 py-4 text-sm font-medium text-gray-900 ${
                    item["Status"] === "DISABLED" ? 'bg-red-50' : ''
                  }`}>{item["Room Type"]}</td>
                  <td className={`px-4 py-4 text-sm text-gray-900 ${
                    item["Status"] === "DISABLED" ? 'bg-red-50' : ''
                  }`}>{item["Room Size"]}</td>
                  <td className={`px-4 py-4 text-sm text-gray-900 ${
                    item["Status"] === "DISABLED" ? 'bg-red-50' : ''
                  }`}>{item["Max Occupancy"]}</td>
                  <td className={`px-4 py-4 text-sm ${
                    item["Status"] === "DISABLED" ? 'bg-red-50' : ''
                  }`}>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      item["Status"] === "ENABLED" ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item["Status"]}
                    </span>
                  </td>
                  <td className={`px-4 py-4 text-sm font-medium ${
                    item["Status"] === "DISABLED" ? 'bg-red-50' : ''
                  }`}>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(item.RoomID); }} className="text-red-600 hover:text-red-900">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </>
              )}
              {type === 'contracts' && (
                <>
                  <td className={`px-4 py-4 text-sm font-medium ${isExpired ? 'text-gray-500' : 'text-gray-900'}`}>{item["Name"]}</td>
                  <td className={`px-4 py-4 text-sm ${isExpired ? 'text-gray-500' : 'text-gray-900'}`}>{item["Type"]}</td>
                  <td className={`px-4 py-4 text-sm ${isExpired ? 'text-gray-500' : 'text-gray-900'}`}>{formatDate(item["From Date"])} to {formatDate(item["To Date"])}</td>
                  <td className="px-4 py-4 text-sm font-medium">
                    <button onClick={(e) => { e.stopPropagation(); onDelete(item.ContractID); }} className="text-red-600 hover:text-red-900">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </>
              )}
              {type === 'seasons' && (
                <>
                  <td className={`px-4 py-4 text-sm font-medium ${isExpired ? 'text-gray-500' : 'text-gray-900'}`}>{item["Season"]}</td>
                  <td className={`px-4 py-4 text-sm ${isExpired ? 'text-gray-500' : 'text-gray-900'}`}>{formatDate(item["From Date"])} to {formatDate(item["To Date"])}</td>
                  <td className={`px-4 py-4 text-sm ${isExpired ? 'text-gray-500' : 'text-gray-900'}`}>{item["Surcharge"]}</td>
                  <td className="px-4 py-4 text-sm font-medium">
                    <button onClick={(e) => { e.stopPropagation(); onDelete(item.SeasonID); }} className="text-red-600 hover:text-red-900">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ItemForm: React.FC<{
  type: string;
  data?: any;
  hotelId?: number;
  hotel?: Hotel;
  onSave: (data: any) => void;
  onCancel: () => void;
}> = ({ type, data, hotelId, hotel, onSave, onCancel }) => {
  const [formData, setFormData] = useState<any>(() => {
    if (type === 'rooms') {
      return data ? {
        room_id: data.RoomID,
        room_type: data["Room Type"] || '',
        room_size: data["Room Size"] || '',
        max_occupancy: data["Max Occupancy"] || '',
        status: data["Status"] || 'ENABLED'
      } : {
        room_type: '',
        room_size: '',
        max_occupancy: '',
        status: 'ENABLED'
      };
    }
    
    if (type === 'contracts') {
      return data ? {
        contract_id: data.ContractID,
        contract_name: data["Name"] || '',
        contract_type: data["Type"] || 'REGULAR',
        from_date: data["From Date"] || '',
        to_date: data["To Date"] || '',
        status: data["Status"] || 'ENABLED'
      } : {
        contract_name: '',
        contract_type: 'REGULAR',
        from_date: '',
        to_date: '',
        status: 'ENABLED'
      };
    }
    
    if (type === 'seasons') {
      return data ? {
        season_id: data.SeasonID,
        season_name: data["Season"] || '',
        from_date: data["From Date"] || '',
        to_date: data["To Date"] || '',
        surcharge: data["Surcharge"] || 0,
        single_surcharge_for_all_rooms: data["Surcharge Type"] === "SAME_FOR_ALL_ROOMS",
        status: data["Status"] || 'ENABLED'
      } : {
        season_name: '',
        from_date: '',
        to_date: '',
        surcharge: 0,
        single_surcharge_for_all_rooms: true,
        status: 'ENABLED'
      };
    }
    
    return {};
  });

  const [contractFile, setContractFile] = useState<File | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(type === 'contracts' ? { ...formData, _contractFile: contractFile } : formData);
  };

  return (
    <div className="space-y-6">
      {hotel && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Building className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-medium text-blue-900">{hotel["Hotel Name"]}</h3>
              <p className="text-sm text-blue-700">{hotel["Area"]} • {hotel["Star"]} <span className="text-yellow-500">{'★'.repeat(parseInt(hotel["Star"].replace(' Star', '')) || 0)}</span></p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {type === 'rooms' && (
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Room Type"
              value={formData.room_type || ''}
              onChange={(e) => setFormData({...formData, room_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Room Size"
                value={formData.room_size || ''}
                onChange={(e) => setFormData({...formData, room_size: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                placeholder="Max Occupancy"
                value={formData.max_occupancy || ''}
                onChange={(e) => setFormData({...formData, max_occupancy: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={formData.status || 'ENABLED'}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ENABLED">Enabled</option>
              <option value="DISABLED">Disabled</option>
            </select>
          </div>
        )}

        {type === 'contracts' && (
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Contract Name"
              value={formData.contract_name || ''}
              onChange={(e) => setFormData({...formData, contract_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
            <select
              value={formData.contract_type || 'REGULAR'}
              onChange={(e) => setFormData({...formData, contract_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="REGULAR">Regular</option>
              <option value="CAMPAIGN">Campaign</option>
              <option value="DYNAMIC">Dynamic</option>
            </select>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                value={formData.from_date || ''}
                onChange={(e) => setFormData({...formData, from_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="date"
                value={formData.to_date || ''}
                onChange={(e) => setFormData({...formData, to_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <select
              value={formData.status || 'ENABLED'}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ENABLED">Enabled</option>
              <option value="DISABLED">Disabled</option>
            </select>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contract File</label>
              {data?.["Contract File"] && !contractFile && (
                <div className="flex items-center gap-2 mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <span className="text-sm text-green-800">📎 {data["Contract File"].split('/').pop()}</span>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const res = await api.get(`/hotel-management/${hotelId}/contracts/${data.ContractID}/file`);
                        if (res.data?.success && res.data.urls?.download) {
                          window.open(res.data.urls.download, '_blank');
                        } else {
                          alert('File not available');
                        }
                      } catch { alert('Error downloading file'); }
                    }}
                  >Download</button>
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContractFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {contractFile && <p className="text-xs text-gray-500 mt-1">New file: {contractFile.name}</p>}
            </div>
            {data && hotelId && (
              <ContractRoomPricing
                hotelId={hotelId}
                contractId={data.ContractID}
                onPricesChange={(prices: any[]) => setFormData({...formData, room_prices: prices})}
              />
            )}
          </div>
        )}

        {type === 'seasons' && (
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Season Name"
              value={formData.season_name || ''}
              onChange={(e) => setFormData({...formData, season_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                value={formData.from_date || ''}
                onChange={(e) => setFormData({...formData, from_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
              <input
                type="date"
                value={formData.to_date || ''}
                onChange={(e) => setFormData({...formData, to_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div className="space-y-3">
              <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  name="surcharge_type"
                  checked={formData.single_surcharge_for_all_rooms === true}
                  onChange={() => setFormData({...formData, single_surcharge_for_all_rooms: true})}
                />
                <span>Same surcharge for all rooms</span>
              </label>
              
              <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer">
                <input
                  type="radio"
                  name="surcharge_type"
                  checked={formData.single_surcharge_for_all_rooms === false}
                  onChange={() => setFormData({...formData, single_surcharge_for_all_rooms: false})}
                />
                <span>Different surcharge for each room</span>
              </label>
            </div>
            
            {formData.single_surcharge_for_all_rooms && (
              <input
                type="number"
                placeholder="Surcharge Amount"
                value={formData.surcharge || ''}
                onChange={(e) => setFormData({...formData, surcharge: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                step="0.01"
              />
            )}
            
            {!formData.single_surcharge_for_all_rooms && hotelId && (
              <RoomSurchargeEditor
                hotelId={hotelId}
                onSurchargesChange={(surcharges: any[]) => setFormData({...formData, room_surcharges: surcharges})}
              />
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

// Add Hotel Form Component
const AddHotelForm: React.FC<{
  onSave: () => void;
  onCancel: () => void;
}> = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    hotel_name: '',
    area: '',
    star_rating: '3 Star',
    address: '',
    additional_notes: '',
    kids_min_age_for_breakfast: 0,
    kids_breakfast_cost: 0,
    extra_bed_min_age: 0,
    extra_bed_cost: 0,
    is_dynamic_pricing: false,
    status: 'ENABLED'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/hotel-management/', formData);
      if (response.data.success) {
        alert('✅ Hotel added successfully!');
        onSave();
      }
    } catch (error: any) {
      console.error('Error adding hotel:', error);
      alert(`❌ Error: ${error?.response?.data?.message || 'Failed to add hotel'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name *</label>
          <input
            type="text"
            value={formData.hotel_name}
            onChange={(e) => setFormData({...formData, hotel_name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Area *</label>
          <input
            type="text"
            value={formData.area}
            onChange={(e) => setFormData({...formData, area: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Star Rating</label>
          <select
            value={formData.star_rating}
            onChange={(e) => setFormData({...formData, star_rating: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="No Star">No Star</option>
            <option value="2 Star">2 Star</option>
            <option value="3 Star">3 Star</option>
            <option value="4 Star">4 Star</option>
            <option value="5 Star">5 Star</option>
            <option value="Premium">Premium</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="ENABLED">Enabled</option>
            <option value="DISABLED">Disabled</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {loading ? 'Adding...' : 'Add Hotel'}
        </button>
      </div>
    </form>
  );
};

// Hotel Details Form Component
const HotelDetailsForm: React.FC<{
  hotel: Hotel;
  onSave: () => void;
  onDelete: () => void;
}> = ({ hotel, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    hotel_id: hotel["Hotel ID"],
    hotel_name: hotel["Hotel Name"],
    area: hotel["Area"],
    star_rating: hotel["Star"],
    address: hotel["Address"] || '',
    additional_notes: hotel["Notes"] || '',
    kids_min_age_for_breakfast: hotel["Kids Min Age BF"] || 0,
    kids_breakfast_cost: hotel["Kids BF Cost"] || 0,
    extra_bed_min_age: hotel["EB Min Age"] || 0,
    extra_bed_cost: hotel["EB Cost"] || 0,
    is_dynamic_pricing: hotel["Contract/Dynamic"] === "Dynamic",
    status: hotel["Status"]
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData({
      hotel_id: hotel["Hotel ID"],
      hotel_name: hotel["Hotel Name"],
      area: hotel["Area"],
      star_rating: hotel["Star"],
      address: hotel["Address"] || '',
      additional_notes: hotel["Notes"] || '',
      kids_min_age_for_breakfast: hotel["Kids Min Age BF"] || 0,
      kids_breakfast_cost: hotel["Kids BF Cost"] || 0,
      extra_bed_min_age: hotel["EB Min Age"] || 0,
      extra_bed_cost: hotel["EB Cost"] || 0,
      is_dynamic_pricing: hotel["Contract/Dynamic"] === "Dynamic",
      status: hotel["Status"]
    });
  }, [hotel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/hotel-management/', formData);
      onSave();
    } catch (error) {
      console.error('Error saving hotel:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h3 className="text-lg font-medium text-gray-900 mb-6">Hotel Details</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hotel Name</label>
            <input
              type="text"
              value={formData.hotel_name}
              onChange={(e) => setFormData({...formData, hotel_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Area</label>
            <input
              type="text"
              value={formData.area}
              onChange={(e) => setFormData({...formData, area: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Star Rating</label>
            <select
              value={formData.star_rating}
              onChange={(e) => setFormData({...formData, star_rating: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="No Star">No Star</option>
              <option value="2 Star">2 Star</option>
              <option value="3 Star">3 Star</option>
              <option value="4 Star">4 Star</option>
              <option value="5 Star">5 Star</option>
              <option value="Premium">Premium</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ENABLED">Enabled</option>
              <option value="DISABLED">Disabled</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
          <textarea
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Enter hotel address..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kids Min Age for Breakfast</label>
            <input
              type="number"
              value={formData.kids_min_age_for_breakfast}
              onChange={(e) => setFormData({...formData, kids_min_age_for_breakfast: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kids Breakfast Cost</label>
            <input
              type="number"
              value={formData.kids_breakfast_cost}
              onChange={(e) => setFormData({...formData, kids_breakfast_cost: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Extra Bed Min Age</label>
            <input
              type="number"
              value={formData.extra_bed_min_age}
              onChange={(e) => setFormData({...formData, extra_bed_min_age: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Extra Bed Cost</label>
            <input
              type="number"
              value={formData.extra_bed_cost}
              onChange={(e) => setFormData({...formData, extra_bed_cost: parseInt(e.target.value) || 0})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Type</label>
          <select
            value={formData.is_dynamic_pricing ? "Dynamic" : "Contract"}
            onChange={(e) => setFormData({...formData, is_dynamic_pricing: e.target.value === "Dynamic"})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="Contract">Contract</option>
            <option value="Dynamic">Dynamic</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
          <textarea
            value={formData.additional_notes}
            onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Enter additional notes..."
          />
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={onDelete}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Delete Hotel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Contract Room Pricing Component
const ContractRoomPricing: React.FC<{
  hotelId: number;
  contractId?: number;
  onPricesChange: (prices: any[]) => void;
}> = ({ hotelId, contractId, onPricesChange }) => {
  const [roomPrices, setRoomPrices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoomPrices();
  }, [hotelId, contractId]);

  const loadRoomPrices = async () => {
    try {
      setLoading(true);
      if (contractId) {
        const response = await api.get(`/hotel-management/${hotelId}/contracts/${contractId}/room-prices`);
        if (response.data.success) {
          setRoomPrices(response.data.data);
          onPricesChange(response.data.data);
        }
      } else {
        const response = await api.get(`/hotel-management/${hotelId}/rooms`);
        if (response.data.success) {
          const roomData = response.data.data.map((room: any) => ({
            RoomID: room.RoomID,
            Room: room["Room Type"],
            "Base Price": 0
          }));
          setRoomPrices(roomData);
          onPricesChange(roomData);
        }
      }
    } catch (error) {
      console.error('Error loading room prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const updatePrice = (roomId: number, price: number) => {
    const updated = roomPrices.map(room => 
      room.RoomID === roomId ? { ...room, "Base Price": price } : room
    );
    setRoomPrices(updated);
    onPricesChange(updated);
  };

  if (loading) return <div className="text-sm text-gray-500">Loading...</div>;

  return (
    <div className="space-y-3">
      <h4 className="text-lg font-medium text-gray-900">Room Prices</h4>
      {roomPrices.map((room) => (
        <div key={room.RoomID} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="font-medium text-gray-900">{room.Room}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">$</span>
            <input
              type="number"
              value={room["Base Price"] || ''}
              onChange={(e) => updatePrice(room.RoomID, parseFloat(e.target.value) || 0)}
              className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Room Surcharge Editor Component
const RoomSurchargeEditor: React.FC<{
  hotelId: number;
  onSurchargesChange: (surcharges: any[]) => void;
}> = ({ hotelId, onSurchargesChange }) => {
  const [surcharges, setSurcharges] = useState<any[]>([]);

  useEffect(() => {
    loadRooms();
  }, [hotelId]);

  const loadRooms = async () => {
    try {
      const response = await api.get(`/hotel-management/${hotelId}/rooms`);
      if (response.data.success) {
        const roomData = response.data.data;
        const initialSurcharges = roomData.map((room: any) => ({
          room_id: room.RoomID,
          room_type: room["Room Type"],
          surcharge: 0
        }));
        setSurcharges(initialSurcharges);
        onSurchargesChange(initialSurcharges);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const updateSurcharge = (roomId: number, value: number) => {
    const updated = surcharges.map(s => 
      s.room_id === roomId ? { ...s, surcharge: value } : s
    );
    setSurcharges(updated);
    onSurchargesChange(updated);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Room Surcharges</label>
      {surcharges.map((room) => (
        <div key={room.room_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="font-medium text-gray-900">{room.room_type}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">$</span>
            <input
              type="number"
              value={room.surcharge}
              onChange={(e) => updateSurcharge(room.room_id, parseFloat(e.target.value) || 0)}
              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              step="0.01"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default HotelManagementV2;